import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type {
  MatchState,
  MatchSettings,
  DeliveryRecord,
  ExtraType,
  TeamRecord,
  InningData,
} from '#/types/cricket'
import { saveMatchFn } from '#/server/match.functions'

interface CricketStore {
  currentMatch: MatchState | null
  savedMatches: MatchState[]
  createdMatchIds: string[]
  teamsList: TeamRecord[]
  savedPlayerNames: string[]
  matchSettings: MatchSettings
  historyStack: MatchState[]

  // Team actions
  addTeam: (name: string) => void
  updateTeam: (id: string, name: string) => void
  deleteTeam: (id: string) => void
  addPlayerToTeam: (teamIdOrName: string, playerName: string) => void
  removePlayerFromTeam: (teamIdOrName: string, playerName: string) => void
  updatePlayerInTeam: (teamIdOrName: string, oldName: string, newName: string) => void

  // Auto-saved players & teams
  registerPlayer: (name: string) => void
  registerTeam: (name: string) => void

  // Match actions
  updateSettings: (settings: Partial<MatchSettings>) => void
  initNewMatch: (params: {
    hostTeam: string
    visitorTeam: string
    tossWinner: 'host' | 'visitor'
    tossDecision: 'bat' | 'bowl'
    totalOvers: number
    playersPerTeam?: number
  }) => void
  setOpeningPlayers: (striker: string, nonStriker: string, openingBowler: string) => void
  startSecondInnings: (striker: string, nonStriker: string, openingBowler: string) => void
  changeBowler: (bowlerName: string) => void
  swapBatsmen: () => void
  retireBatsman: (retiredPlayerName: string, newBatsmanName: string) => void
  updateMatchSettingsFirstInnings: (params: { totalOvers: number; playersPerTeam: number }) => {
    inningsCompleted: boolean
    runsNeeded: number
    overs: number
    rrr: string
    chasingTeam: string
  } | undefined
  recordDelivery: (params: {
    runs: number
    extraType: ExtraType
    isWicket: boolean
    wicketType?: 'bowled' | 'caught' | 'run_out' | 'lbw' | 'stumped' | 'hit_wicket'
    dismissedPlayer?: string
    newBatsmanName?: string
  }) => {
    overCompleted: boolean
    inningsCompleted: boolean
    matchCompleted: boolean
    target?: number
    reqRuns?: number
    reqBalls?: number
    winnerText?: string
    winnerTeam?: string
  }
  undoLastDelivery: () => void
  deleteMatch: (id: string) => void
  loadMatchById: (id: string) => MatchState | undefined
  syncMatchState: (match: MatchState) => void
  syncOnline: () => Promise<boolean>
}

const defaultSettings: MatchSettings = {
  playersPerTeam: 11,
  noBallEnabled: true,
  noBallReball: true,
  noBallRun: 1,
  wideBallEnabled: true,
  wideBallReball: true,
  wideBallRun: 1,
}

const defaultTeams: TeamRecord[] = []

let syncChannel: BroadcastChannel | null = null
if (typeof window !== 'undefined' && 'BroadcastChannel' in window) {
  syncChannel = new BroadcastChannel('cricket_scorer_channel')
}

// Background sync to Cloudflare D1
function syncToRemoteD1(match: MatchState) {
  saveMatchFn({ data: match }).catch((err) => {
    console.error('Failed to sync match to D1 in background:', err)
  })
}

export const useCricketStore = create<CricketStore>()(
  persist(
    (set, get) => {
      if (syncChannel) {
        syncChannel.onmessage = (event) => {
          if (event.data?.type === 'MATCH_UPDATE' && event.data.payload) {
            set((state) => {
              if (state.currentMatch?.id === event.data.payload.id) {
                return { currentMatch: event.data.payload }
              }
              return state
            })
          }
        }
      }

      const broadcastMatch = (match: MatchState) => {
        if (syncChannel) {
          syncChannel.postMessage({ type: 'MATCH_UPDATE', payload: match })
        }
        syncToRemoteD1(match)
      }

      const emptyInning = (battingTeam: string, bowlingTeam: string): InningData => ({
        battingTeam,
        bowlingTeam,
        totalRuns: 0,
        totalWickets: 0,
        legalBallsDelivered: 0,
        extras: { byes: 0, legByes: 0, wides: 0, noBalls: 0, penalty: 0 },
        batsmen: {},
        bowlers: {},
        currentOverDeliveries: [],
        deliveryHistory: [],
        striker: '',
        nonStriker: '',
        currentBowler: '',
        currentPartnership: { runs: 0, balls: 0, player1Runs: 0, player2Runs: 0 },
      })

      const ensureTeamRegistered = (name: string, stateTeams: TeamRecord[]): TeamRecord[] => {
        const clean = name.trim()
        if (!clean) return stateTeams
        const exists = stateTeams.some((t) => t.name.toLowerCase() === clean.toLowerCase())
        if (exists) return stateTeams

        const shortCode =
          clean
            .split(' ')
            .map((w) => w[0])
            .join('')
            .toUpperCase()
            .slice(0, 2) || clean.slice(0, 2).toUpperCase()

        const newTeam: TeamRecord = {
          id: `team_${Date.now()}_${Math.random().toString(36).substring(2, 6)}`,
          name: clean,
          shortCode,
          matchesCount: 0,
          wonCount: 0,
          lostCount: 0,
          colorIndex: stateTeams.length % 5,
        }
        return [...stateTeams, newTeam]
      }

      const ensurePlayerRegistered = (name: string, statePlayers: string[]): string[] => {
        const clean = name.trim()
        if (!clean) return statePlayers
        if (statePlayers.some((p) => p.toLowerCase() === clean.toLowerCase())) {
          return statePlayers
        }
        return [...statePlayers, clean]
      }

      const ensurePlayerInTeam = (teamName: string, playerName: string, stateTeams: TeamRecord[]): TeamRecord[] => {
        const cleanTeam = teamName.trim()
        const cleanPlayer = playerName.trim()
        if (!cleanTeam || !cleanPlayer) return stateTeams

        return stateTeams.map((t) => {
          if (t.name.toLowerCase() === cleanTeam.toLowerCase()) {
            const currentPlayers = t.players || []
            if (!currentPlayers.some((p) => p.toLowerCase() === cleanPlayer.toLowerCase())) {
              return { ...t, players: [...currentPlayers, cleanPlayer] }
            }
          }
          return t
        })
      }

      return {
        currentMatch: null,
        savedMatches: [],
        createdMatchIds: [],
        teamsList: defaultTeams,
        savedPlayerNames: [],
        matchSettings: defaultSettings,
        historyStack: [],

        registerPlayer: (name) => {
          set((state) => ({
            savedPlayerNames: ensurePlayerRegistered(name, state.savedPlayerNames),
          }))
        },

        registerTeam: (name) => {
          set((state) => ({
            teamsList: ensureTeamRegistered(name, state.teamsList),
          }))
        },

        addTeam: (name) => {
          set((state) => ({
            teamsList: ensureTeamRegistered(name, state.teamsList),
          }))
        },

        updateTeam: (id, name) => {
          const shortCode =
            name
              .split(' ')
              .map((w) => w[0])
              .join('')
              .toUpperCase()
              .slice(0, 2) || name.slice(0, 2).toUpperCase()
          set((state) => ({
            teamsList: state.teamsList.map((t) => (t.id === id ? { ...t, name, shortCode } : t)),
          }))
        },

        deleteTeam: (id) => {
          set((state) => ({ teamsList: state.teamsList.filter((t) => t.id !== id) }))
        },

        addPlayerToTeam: (teamIdOrName, playerName) => {
          const cleanPlayer = playerName.trim()
          if (!cleanPlayer) return
          set((state) => {
            const updatedTeams = state.teamsList.map((t) => {
              if (t.id === teamIdOrName || t.name.toLowerCase() === teamIdOrName.toLowerCase()) {
                const currentPlayers = t.players || []
                if (!currentPlayers.some((p) => p.toLowerCase() === cleanPlayer.toLowerCase())) {
                  return { ...t, players: [...currentPlayers, cleanPlayer] }
                }
              }
              return t
            })
            const updatedSavedPlayers = ensurePlayerRegistered(cleanPlayer, state.savedPlayerNames)
            return {
              teamsList: updatedTeams,
              savedPlayerNames: updatedSavedPlayers,
            }
          })
        },

        removePlayerFromTeam: (teamIdOrName, playerName) => {
          set((state) => ({
            teamsList: state.teamsList.map((t) => {
              if (t.id === teamIdOrName || t.name.toLowerCase() === teamIdOrName.toLowerCase()) {
                return {
                  ...t,
                  players: (t.players || []).filter((p) => p.toLowerCase() !== playerName.toLowerCase()),
                }
              }
              return t
            }),
          }))
        },

        updatePlayerInTeam: (teamIdOrName, oldName, newName) => {
          const cleanNew = newName.trim()
          if (!cleanNew) return
          set((state) => ({
            teamsList: state.teamsList.map((t) => {
              if (t.id === teamIdOrName || t.name.toLowerCase() === teamIdOrName.toLowerCase()) {
                return {
                  ...t,
                  players: (t.players || []).map((p) => (p.toLowerCase() === oldName.toLowerCase() ? cleanNew : p)),
                }
              }
              return t
            }),
            savedPlayerNames: ensurePlayerRegistered(cleanNew, state.savedPlayerNames),
          }))
        },

        deleteMatch: (id) => {
          set((state) => ({
            savedMatches: state.savedMatches.filter((m) => m.id !== id),
            currentMatch: state.currentMatch?.id === id ? null : state.currentMatch,
          }))
        },

        updateSettings: (newSettings) =>
          set((state) => ({
            matchSettings: { ...state.matchSettings, ...newSettings },
          })),

        initNewMatch: ({ hostTeam, visitorTeam, tossWinner, tossDecision, totalOvers, playersPerTeam }) => {
          const matchId = `match_${Date.now()}`
          const battingTeam =
            (tossWinner === 'host' && tossDecision === 'bat') ||
            (tossWinner === 'visitor' && tossDecision === 'bowl')
              ? hostTeam
              : visitorTeam
          const bowlingTeam = battingTeam === hostTeam ? visitorTeam : hostTeam

          const inn1 = emptyInning(battingTeam, bowlingTeam)
          const nowStr =
            new Date().toLocaleDateString('en-GB') +
            ' - ' +
            new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })

          const numPlayers = playersPerTeam || get().matchSettings.playersPerTeam || 11
          const settings = {
            ...get().matchSettings,
            playersPerTeam: numPlayers,
            singlePlayerMode: get().matchSettings.singlePlayerMode || numPlayers === 1,
          }

          const newMatch: MatchState = {
            id: matchId,
            createdAt: nowStr,
            hostTeam,
            visitorTeam,
            tossWinner,
            tossDecision,
            totalOvers,
            settings,
            currentInnings: 1,
            status: 'not_started',
            battingTeam,
            bowlingTeam,
            innings1: inn1,
            striker: '',
            nonStriker: '',
            currentBowler: '',
            totalRuns: 0,
            totalWickets: 0,
            legalBallsDelivered: 0,
            extras: { byes: 0, legByes: 0, wides: 0, noBalls: 0, penalty: 0 },
            batsmen: {},
            bowlers: {},
            currentOverDeliveries: [],
            deliveryHistory: [],
            currentPartnership: { runs: 0, balls: 0, player1Runs: 0, player2Runs: 0 },
            tokens: {
              viewToken: `v_${Math.random().toString(36).substring(2, 9)}`,
              editToken: `e_${Math.random().toString(36).substring(2, 9)}`,
            },
          }

          set((state) => {
            let updatedTeams = ensureTeamRegistered(hostTeam, state.teamsList)
            updatedTeams = ensureTeamRegistered(visitorTeam, updatedTeams)
            const currentCreated = state.createdMatchIds || []
            return {
              currentMatch: newMatch,
              savedMatches: [newMatch, ...state.savedMatches.filter((m) => m.id !== matchId)],
              createdMatchIds: currentCreated.includes(matchId) ? currentCreated : [...currentCreated, matchId],
              teamsList: updatedTeams,
              historyStack: [],
            }
          })
          broadcastMatch(newMatch)
        },

        setOpeningPlayers: (striker, nonStriker, openingBowler) => {
          const match = get().currentMatch
          if (!match) return

          const isSingle = Boolean(match.settings.singlePlayerMode || match.settings.playersPerTeam === 1)
          const batsmenMap: Record<string, any> = {
            [striker]: { name: striker, runs: 0, balls: 0, fours: 0, sixes: 0, isOut: false },
          }
          if (!isSingle && nonStriker) {
            batsmenMap[nonStriker] = { name: nonStriker, runs: 0, balls: 0, fours: 0, sixes: 0, isOut: false }
          }

          const updatedMatch: MatchState = {
            ...match,
            status: 'live',
            striker,
            nonStriker: isSingle ? '' : nonStriker,
            currentBowler: openingBowler,
            batsmen: batsmenMap,
            bowlers: {
              [openingBowler]: { name: openingBowler, legalBalls: 0, maidens: 0, runsConceded: 0, wickets: 0 },
            },
            currentPartnership: {
              runs: 0,
              balls: 0,
              player1Name: striker,
              player1Runs: 0,
              player1Balls: 0,
              player2Name: isSingle ? '' : nonStriker,
              player2Runs: 0,
              player2Balls: 0,
            },
          }
          updatedMatch.innings1 = {
            ...updatedMatch.innings1,
            striker,
            nonStriker: isSingle ? '' : nonStriker,
            currentBowler: openingBowler,
            batsmen: updatedMatch.batsmen,
            bowlers: updatedMatch.bowlers,
            currentPartnership: updatedMatch.currentPartnership,
          }

          set((state) => {
            let players = ensurePlayerRegistered(striker, state.savedPlayerNames)
            if (!isSingle && nonStriker) {
              players = ensurePlayerRegistered(nonStriker, players)
            }
            players = ensurePlayerRegistered(openingBowler, players)

            // Automatically add batsmen to batting team and bowler to bowling team
            let updatedTeams = ensurePlayerInTeam(updatedMatch.battingTeam, striker, state.teamsList)
            if (!isSingle && nonStriker) {
              updatedTeams = ensurePlayerInTeam(updatedMatch.battingTeam, nonStriker, updatedTeams)
            }
            updatedTeams = ensurePlayerInTeam(updatedMatch.bowlingTeam, openingBowler, updatedTeams)

            return {
              currentMatch: updatedMatch,
              savedMatches: state.savedMatches.map((m) => (m.id === updatedMatch.id ? updatedMatch : m)),
              savedPlayerNames: players,
              teamsList: updatedTeams,
            }
          })
          broadcastMatch(updatedMatch)
        },

        startSecondInnings: (striker, nonStriker, openingBowler) => {
          const match = get().currentMatch
          if (!match) return

          const isSingle = Boolean(match.settings.singlePlayerMode || match.settings.playersPerTeam === 1)
          const newBattingTeam = match.innings1.bowlingTeam
          const newBowlingTeam = match.innings1.battingTeam
          const targetRuns = match.innings1.totalRuns + 1

          const batsmenMap: Record<string, any> = {
            [striker]: { name: striker, runs: 0, balls: 0, fours: 0, sixes: 0, isOut: false },
          }
          if (!isSingle && nonStriker) {
            batsmenMap[nonStriker] = { name: nonStriker, runs: 0, balls: 0, fours: 0, sixes: 0, isOut: false }
          }

          const inn2: InningData = {
            battingTeam: newBattingTeam,
            bowlingTeam: newBowlingTeam,
            totalRuns: 0,
            totalWickets: 0,
            legalBallsDelivered: 0,
            extras: { byes: 0, legByes: 0, wides: 0, noBalls: 0, penalty: 0 },
            batsmen: batsmenMap,
            bowlers: {
              [openingBowler]: { name: openingBowler, legalBalls: 0, maidens: 0, runsConceded: 0, wickets: 0 },
            },
            currentOverDeliveries: [],
            deliveryHistory: [],
            striker,
            nonStriker: isSingle ? '' : nonStriker,
            currentBowler: openingBowler,
            currentPartnership: {
              runs: 0,
              balls: 0,
              player1Name: striker,
              player1Runs: 0,
              player1Balls: 0,
              player2Name: isSingle ? '' : nonStriker,
              player2Runs: 0,
              player2Balls: 0,
            },
          }

          const updatedMatch: MatchState = {
            ...match,
            currentInnings: 2,
            targetRuns,
            battingTeam: newBattingTeam,
            bowlingTeam: newBowlingTeam,
            innings2: inn2,
            striker,
            nonStriker,
            currentBowler: openingBowler,
            previousBowler: undefined,
            totalRuns: 0,
            totalWickets: 0,
            legalBallsDelivered: 0,
            extras: inn2.extras,
            batsmen: inn2.batsmen,
            bowlers: inn2.bowlers,
            currentOverDeliveries: [],
            deliveryHistory: [],
            currentPartnership: inn2.currentPartnership,
          }

          set((state) => {
            let players = ensurePlayerRegistered(striker, state.savedPlayerNames)
            players = ensurePlayerRegistered(nonStriker, players)
            players = ensurePlayerRegistered(openingBowler, players)

            let updatedTeams = ensurePlayerInTeam(updatedMatch.battingTeam, striker, state.teamsList)
            if (!isSingle && nonStriker) {
              updatedTeams = ensurePlayerInTeam(updatedMatch.battingTeam, nonStriker, updatedTeams)
            }
            updatedTeams = ensurePlayerInTeam(updatedMatch.bowlingTeam, openingBowler, updatedTeams)

            return {
              currentMatch: updatedMatch,
              savedMatches: state.savedMatches.map((m) => (m.id === updatedMatch.id ? updatedMatch : m)),
              savedPlayerNames: players,
              teamsList: updatedTeams,
            }
          })
          broadcastMatch(updatedMatch)
        },

        changeBowler: (bowlerName) => {
          const match = get().currentMatch
          if (!match) return

          const cleanBowler = bowlerName.trim()
          if (!cleanBowler) return
          if (cleanBowler.toLowerCase() === match.currentBowler.toLowerCase()) {
            return
          }

          const bowlers = { ...match.bowlers }
          if (!bowlers[cleanBowler]) {
            bowlers[cleanBowler] = { name: cleanBowler, legalBalls: 0, maidens: 0, runsConceded: 0, wickets: 0 }
          }

          const updatedMatch: MatchState = {
            ...match,
            previousBowler: match.currentBowler,
            currentBowler: cleanBowler,
            overPendingBowler: false,
            bowlers,
            currentOverDeliveries: [],
          }

          if (updatedMatch.currentInnings === 1) {
            updatedMatch.innings1 = {
              ...updatedMatch.innings1,
              previousBowler: match.currentBowler,
              currentBowler: cleanBowler,
              bowlers,
              currentOverDeliveries: [],
            }
          } else if (updatedMatch.innings2) {
            updatedMatch.innings2 = {
              ...updatedMatch.innings2,
              previousBowler: match.currentBowler,
              currentBowler: cleanBowler,
              bowlers,
              currentOverDeliveries: [],
            }
          }

          set((state) => {
            const players = ensurePlayerRegistered(cleanBowler, state.savedPlayerNames)
            const updatedTeams = ensurePlayerInTeam(match.bowlingTeam, cleanBowler, state.teamsList)
            return {
              currentMatch: updatedMatch,
              savedMatches: state.savedMatches.map((m) => (m.id === updatedMatch.id ? updatedMatch : m)),
              savedPlayerNames: players,
              teamsList: updatedTeams,
            }
          })
          broadcastMatch(updatedMatch)
        },

        swapBatsmen: () => {
          const match = get().currentMatch
          if (!match) return
          const updatedMatch: MatchState = {
            ...match,
            striker: match.nonStriker,
            nonStriker: match.striker,
          }
          set((state) => ({
            currentMatch: updatedMatch,
            savedMatches: state.savedMatches.map((m) => (m.id === updatedMatch.id ? updatedMatch : m)),
          }))
          broadcastMatch(updatedMatch)
        },

        retireBatsman: (retiredPlayerName, newBatsmanName) => {
          const match = get().currentMatch
          if (!match) return

          const isStriker = retiredPlayerName === match.striker
          const batsmen = { ...match.batsmen }
          if (batsmen[retiredPlayerName]) {
            batsmen[retiredPlayerName] = {
              ...batsmen[retiredPlayerName],
              isOut: true,
              dismissal: 'retired hurt',
            }
          }
          batsmen[newBatsmanName] = { name: newBatsmanName, runs: 0, balls: 0, fours: 0, sixes: 0, isOut: false }

          const updatedMatch: MatchState = {
            ...match,
            striker: isStriker ? newBatsmanName : match.striker,
            nonStriker: !isStriker ? newBatsmanName : match.nonStriker,
            batsmen,
          }

          set((state) => {
            const players = ensurePlayerRegistered(newBatsmanName, state.savedPlayerNames)
            const updatedTeams = ensurePlayerInTeam(match.battingTeam, newBatsmanName, state.teamsList)
            return {
              currentMatch: updatedMatch,
              savedMatches: state.savedMatches.map((m) => (m.id === updatedMatch.id ? updatedMatch : m)),
              savedPlayerNames: players,
              teamsList: updatedTeams,
            }
          })
          broadcastMatch(updatedMatch)
        },

        updateMatchSettingsFirstInnings: ({ totalOvers, playersPerTeam }) => {
          const match = get().currentMatch
          if (!match) return
          // Only editable in the first innings
          if (match.currentInnings !== 1) return

          const isSingle = Boolean(playersPerTeam === 1 || match.settings.singlePlayerMode)
          const updatedSettings: MatchSettings = {
            ...match.settings,
            playersPerTeam,
            singlePlayerMode: isSingle,
          }

          const updatedMatch: MatchState = {
            ...match,
            totalOvers,
            settings: updatedSettings,
            innings1: {
              ...match.innings1,
            },
          }

          set((state) => ({
            currentMatch: updatedMatch,
            savedMatches: state.savedMatches.map((m) => (m.id === updatedMatch.id ? updatedMatch : m)),
          }))
          broadcastMatch(updatedMatch)

          // Check if innings is now completed because the new overs limit was reached or exceeded
          const isMaxOvers = match.legalBallsDelivered >= totalOvers * 6
          const maxAllowedWickets = Math.max(1, playersPerTeam - 1)
          const isAllOut = match.totalWickets >= maxAllowedWickets

          return {
            inningsCompleted: isMaxOvers || isAllOut,
            runsNeeded: match.totalRuns + 1,
            overs: totalOvers,
            rrr: totalOvers > 0 ? ((match.totalRuns + 1) / totalOvers).toFixed(2) : '0.00',
            chasingTeam: match.bowlingTeam,
          }
        },

        recordDelivery: ({ runs, extraType, isWicket, wicketType, dismissedPlayer, newBatsmanName }) => {
          const match = get().currentMatch
          if (!match) return { overCompleted: false, inningsCompleted: false, matchCompleted: false }

          // Strictly enforce: if the match is already completed, do not allow further balls
          if (match.status === 'completed') {
            return { overCompleted: false, inningsCompleted: false, matchCompleted: true }
          }

          // Strictly enforce: if the current innings has already reached maximum overs or all-out, reject further deliveries
          const teamMaxWickets = Math.max(1, match.settings.playersPerTeam - 1)
          const isCurrentInningsOver =
            match.legalBallsDelivered >= match.totalOvers * 6 || match.totalWickets >= teamMaxWickets

          if (isCurrentInningsOver) {
            return {
              overCompleted: true,
              inningsCompleted: match.currentInnings === 1,
              matchCompleted: match.currentInnings === 2,
            }
          }

          // Strictly enforce: if an over has completed and a new bowler hasn't been chosen yet, reject recording delivery
          if (match.overPendingBowler) {
            return { overCompleted: true, inningsCompleted: false, matchCompleted: false }
          }

          const historyStack = [...get().historyStack, JSON.parse(JSON.stringify(match))]

          let isLegal = true
          let extraRuns = 0
          const extras = { ...match.extras }

          if (extraType === 'wide') {
            isLegal = !match.settings.wideBallReball
            extraRuns = match.settings.wideBallRun + runs
            extras.wides += extraRuns
          } else if (extraType === 'no_ball') {
            isLegal = !match.settings.noBallReball
            extraRuns = match.settings.noBallRun
            extras.noBalls += extraRuns
          } else if (extraType === 'bye') {
            isLegal = true
            extraRuns = runs
            extras.byes += extraRuns
          } else if (extraType === 'leg_bye') {
            isLegal = true
            extraRuns = runs
            extras.legByes += extraRuns
          }

          const runsFromBat = extraType === 'none' || extraType === 'no_ball' ? runs : 0
          const totalDeliveryRuns = extraType === 'wide' || extraType === 'no_ball' ? extraRuns + runsFromBat : runs

          let displayBadge = runs.toString()
          if (extraType === 'wide') displayBadge = runs > 0 ? `${runs}wd` : 'wd'
          else if (extraType === 'no_ball') displayBadge = runs > 0 ? `${runs}nb` : 'nb'
          else if (extraType === 'bye') displayBadge = `${runs}b`
          else if (extraType === 'leg_bye') displayBadge = `${runs}lb`
          if (isWicket) displayBadge = runs > 0 ? `W+${runs}` : 'W'

          const overIndex = Math.floor(match.legalBallsDelivered / 6) + 1
          const delivery: DeliveryRecord = {
            id: `del_${Date.now()}_${Math.random()}`,
            inning: match.currentInnings,
            overIndex,
            ballNumberInOver: isLegal ? (match.legalBallsDelivered % 6) + 1 : match.legalBallsDelivered % 6,
            striker: match.striker,
            nonStriker: match.nonStriker,
            bowler: match.currentBowler,
            runsScored: totalDeliveryRuns,
            extraType,
            extraRuns,
            isLegal,
            isWicket,
            wicketType: isWicket ? wicketType || 'bowled' : undefined,
            dismissedPlayer: isWicket ? dismissedPlayer || match.striker : undefined,
            displayBadge,
          }

          // Batsman update
          const batsmen = { ...match.batsmen }
          const activeStriker = batsmen[match.striker] || {
            name: match.striker,
            runs: 0,
            balls: 0,
            fours: 0,
            sixes: 0,
            isOut: false,
          }

          const updatedStriker = {
            ...activeStriker,
            runs: activeStriker.runs + runsFromBat,
            balls: extraType !== 'wide' ? activeStriker.balls + 1 : activeStriker.balls,
            fours: runsFromBat === 4 ? activeStriker.fours + 1 : activeStriker.fours,
            sixes: runsFromBat === 6 ? activeStriker.sixes + 1 : activeStriker.sixes,
          }

          if (isWicket && (!dismissedPlayer || dismissedPlayer === match.striker)) {
            updatedStriker.isOut = true
            updatedStriker.dismissal = wicketType || 'bowled'
          }
          batsmen[match.striker] = updatedStriker

          if (isWicket && dismissedPlayer === match.nonStriker && batsmen[match.nonStriker]) {
            batsmen[match.nonStriker] = {
              ...batsmen[match.nonStriker],
              isOut: true,
              dismissal: 'run out',
            }
          }

          let nextStriker = match.striker
          let nextNonStriker = match.nonStriker
          if (isWicket && newBatsmanName) {
            batsmen[newBatsmanName] = {
              name: newBatsmanName,
              runs: 0,
              balls: 0,
              fours: 0,
              sixes: 0,
              isOut: false,
            }
            if (dismissedPlayer === match.nonStriker) {
              nextNonStriker = newBatsmanName
            } else {
              nextStriker = newBatsmanName
            }
          }

          // Bowler update
          const bowlers = { ...match.bowlers }
          const activeBowler = bowlers[match.currentBowler] || {
            name: match.currentBowler,
            legalBalls: 0,
            maidens: 0,
            runsConceded: 0,
            wickets: 0,
          }

          const bowlerRunsConceded = extraType === 'bye' || extraType === 'leg_bye' ? 0 : totalDeliveryRuns
          bowlers[match.currentBowler] = {
            ...activeBowler,
            legalBalls: isLegal ? activeBowler.legalBalls + 1 : activeBowler.legalBalls,
            runsConceded: activeBowler.runsConceded + bowlerRunsConceded,
            wickets: isWicket && wicketType !== 'run_out' ? activeBowler.wickets + 1 : activeBowler.wickets,
          }

          const newLegalBalls = isLegal ? match.legalBallsDelivered + 1 : match.legalBallsDelivered
          const newTotalRuns = match.totalRuns + totalDeliveryRuns
          const newTotalWickets = isWicket ? match.totalWickets + 1 : match.totalWickets
          const overCompleted = isLegal && newLegalBalls % 6 === 0

          // Check innings & match completion
          const isMaxOvers = newLegalBalls >= match.totalOvers * 6
          const maxAllowedWickets = Math.max(1, match.settings.playersPerTeam - 1)
          const isAllOut = newTotalWickets >= maxAllowedWickets

          let inningsCompleted = false
          let matchCompleted = false
          let target = match.targetRuns
          let reqRuns = 0
          let reqBalls = 0
          let calculatedWinnerText: string | undefined = match.winnerText
          let calculatedWinnerTeam: string | undefined = match.winnerTeam

          if (match.currentInnings === 1) {
            if (isMaxOvers || isAllOut) {
              inningsCompleted = true
              target = newTotalRuns + 1
            }
          } else {
            // 2nd innings
            reqRuns = Math.max(0, (match.targetRuns || 0) - newTotalRuns)
            reqBalls = Math.max(0, match.totalOvers * 6 - newLegalBalls)
            const targetScore = match.targetRuns || 0

            if (newTotalRuns >= targetScore) {
              matchCompleted = true
              const wicketsRemaining = Math.max(1, maxAllowedWickets - newTotalWickets)
              calculatedWinnerTeam = match.battingTeam
              calculatedWinnerText = `${match.battingTeam} won by ${wicketsRemaining} ${
                wicketsRemaining === 1 ? 'wicket' : 'wickets'
              }.`
            } else if (isMaxOvers || isAllOut) {
              matchCompleted = true
              if (newTotalRuns === targetScore - 1) {
                calculatedWinnerText = 'Match Tied.'
              } else {
                const runsMargin = targetScore - 1 - newTotalRuns
                calculatedWinnerTeam = match.bowlingTeam
                calculatedWinnerText = `${match.bowlingTeam} won by ${runsMargin} ${
                  runsMargin === 1 ? 'run' : 'runs'
                }.`
              }
            }
          }

          const isSinglePlayer = Boolean(match.settings.singlePlayerMode || match.settings.playersPerTeam === 1)

          // Only rotate strike if multi-player match
          if (!isSinglePlayer && nextNonStriker) {
            if (runs % 2 === 1) {
              const temp = nextStriker
              nextStriker = nextNonStriker
              nextNonStriker = temp
            }
            if (overCompleted) {
              const temp = nextStriker
              nextStriker = nextNonStriker
              nextNonStriker = temp
            }
          }

          // Calculate partnership stats accurately by player name
          const p1Name = match.currentPartnership.player1Name || match.striker
          const p2Name = isSinglePlayer ? '' : (match.currentPartnership.player2Name || match.nonStriker)

          let updatedPartnership: MatchState['currentPartnership']

          if (isWicket) {
            if (isAllOut) {
              // Innings or match over
              updatedPartnership = {
                runs: match.currentPartnership.runs + totalDeliveryRuns,
                balls: isLegal ? match.currentPartnership.balls + 1 : match.currentPartnership.balls,
                player1Name: p1Name,
                player1Runs: match.striker === p1Name ? match.currentPartnership.player1Runs + runsFromBat : match.currentPartnership.player1Runs,
                player1Balls: isLegal && match.striker === p1Name ? (match.currentPartnership.player1Balls || 0) + 1 : (match.currentPartnership.player1Balls || 0),
                player2Name: p2Name,
                player2Runs: match.striker === p2Name ? match.currentPartnership.player2Runs + runsFromBat : match.currentPartnership.player2Runs,
                player2Balls: isLegal && match.striker === p2Name ? (match.currentPartnership.player2Balls || 0) + 1 : (match.currentPartnership.player2Balls || 0),
              }
            } else {
              // New partnership starts with surviving batsman and new batsman
              const survivingPlayer = dismissedPlayer === match.striker ? match.nonStriker : match.striker
              const newIncomingPlayer = newBatsmanName || ''
              updatedPartnership = {
                runs: 0,
                balls: 0,
                player1Name: survivingPlayer,
                player1Runs: 0,
                player1Balls: 0,
                player2Name: newIncomingPlayer,
                player2Runs: 0,
                player2Balls: 0,
              }
            }
          } else {
            const isStrikerP1 = match.striker === p1Name
            updatedPartnership = {
              runs: match.currentPartnership.runs + totalDeliveryRuns,
              balls: isLegal ? match.currentPartnership.balls + 1 : match.currentPartnership.balls,
              player1Name: p1Name,
              player1Runs: isStrikerP1
                ? match.currentPartnership.player1Runs + runsFromBat
                : match.currentPartnership.player1Runs,
              player1Balls: isStrikerP1 && isLegal
                ? (match.currentPartnership.player1Balls || 0) + 1
                : (match.currentPartnership.player1Balls || 0),
              player2Name: p2Name,
              player2Runs: !isStrikerP1
                ? match.currentPartnership.player2Runs + runsFromBat
                : match.currentPartnership.player2Runs,
              player2Balls: !isStrikerP1 && isLegal
                ? (match.currentPartnership.player2Balls || 0) + 1
                : (match.currentPartnership.player2Balls || 0),
            }
          }

          const updatedMatch: MatchState = {
            ...match,
            status: matchCompleted ? 'completed' : 'live',
            winnerText: calculatedWinnerText,
            winnerTeam: calculatedWinnerTeam,
            totalRuns: newTotalRuns,
            totalWickets: newTotalWickets,
            legalBallsDelivered: newLegalBalls,
            extras,
            batsmen,
            bowlers,
            striker: nextStriker,
            nonStriker: isSinglePlayer ? '' : nextNonStriker,
            overPendingBowler: overCompleted && !matchCompleted && !inningsCompleted,
            currentOverDeliveries: overCompleted ? [] : [...match.currentOverDeliveries, delivery],
            deliveryHistory: [...match.deliveryHistory, delivery],
            currentPartnership: updatedPartnership,
          }

          if (match.currentInnings === 1) {
            updatedMatch.innings1 = {
              battingTeam: updatedMatch.battingTeam,
              bowlingTeam: updatedMatch.bowlingTeam,
              totalRuns: newTotalRuns,
              totalWickets: newTotalWickets,
              legalBallsDelivered: newLegalBalls,
              extras,
              batsmen,
              bowlers,
              currentOverDeliveries: updatedMatch.currentOverDeliveries,
              deliveryHistory: updatedMatch.deliveryHistory,
              striker: nextStriker,
              nonStriker: nextNonStriker,
              currentBowler: match.currentBowler,
              previousBowler: match.previousBowler,
              currentPartnership: updatedMatch.currentPartnership,
            }
          } else if (updatedMatch.innings2) {
            updatedMatch.innings2 = {
              ...updatedMatch.innings2,
              totalRuns: newTotalRuns,
              totalWickets: newTotalWickets,
              legalBallsDelivered: newLegalBalls,
              extras,
              batsmen,
              bowlers,
              currentOverDeliveries: updatedMatch.currentOverDeliveries,
              deliveryHistory: updatedMatch.deliveryHistory,
              striker: nextStriker,
              nonStriker: nextNonStriker,
              currentBowler: match.currentBowler,
              currentPartnership: updatedMatch.currentPartnership,
            }
          }

          set((state) => {
            let updatedTeams = state.teamsList
            let players = state.savedPlayerNames
            if (newBatsmanName) {
              players = ensurePlayerRegistered(newBatsmanName, players)
              updatedTeams = ensurePlayerInTeam(match.battingTeam, newBatsmanName, updatedTeams)
            }

            // If match just completed, update team records (Won / Lost / Matches count)
            if (matchCompleted && calculatedWinnerTeam) {
              const loserTeam =
                calculatedWinnerTeam === match.hostTeam ? match.visitorTeam : match.hostTeam
              updatedTeams = updatedTeams.map((t) => {
                if (t.name.toLowerCase() === calculatedWinnerTeam?.toLowerCase()) {
                  return {
                    ...t,
                    matchesCount: t.matchesCount + 1,
                    wonCount: t.wonCount + 1,
                  }
                }
                if (t.name.toLowerCase() === loserTeam.toLowerCase()) {
                  return {
                    ...t,
                    matchesCount: t.matchesCount + 1,
                    lostCount: t.lostCount + 1,
                  }
                }
                return t
              })
            }

            return {
              currentMatch: updatedMatch,
              historyStack,
              savedMatches: state.savedMatches.map((m) => (m.id === updatedMatch.id ? updatedMatch : m)),
              teamsList: updatedTeams,
              savedPlayerNames: players,
            }
          })

          broadcastMatch(updatedMatch)

          return {
            overCompleted,
            inningsCompleted,
            matchCompleted,
            target,
            reqRuns,
            reqBalls,
            winnerText: calculatedWinnerText,
            winnerTeam: calculatedWinnerTeam,
          }
        },

        undoLastDelivery: () => {
          const { historyStack } = get()
          if (historyStack.length === 0) return

          const previousMatch = historyStack[historyStack.length - 1]
          const newStack = historyStack.slice(0, -1)

          set((state) => ({
            currentMatch: previousMatch,
            historyStack: newStack,
            savedMatches: state.savedMatches.map((m) => (m.id === previousMatch.id ? previousMatch : m)),
          }))
          broadcastMatch(previousMatch)
        },

        loadMatchById: (id) => get().savedMatches.find((m) => m.id === id),

        syncMatchState: (match) => {
          set((state) => ({
            currentMatch: match,
            savedMatches: [match, ...state.savedMatches.filter((m) => m.id !== match.id)],
          }))
        },

        syncOnline: async () => {
          const state = get()
          if (!state.currentMatch && state.savedMatches.length === 0) return true
          try {
            if (state.currentMatch) {
              await saveMatchFn({ data: state.currentMatch })
            }
            for (const m of state.savedMatches) {
              if (m.id !== state.currentMatch?.id) {
                await saveMatchFn({ data: m })
              }
            }
            return true
          } catch (err) {
            console.error('Manual online sync failed:', err)
            return false
          }
        },
      }
    },
    {
      name: 'cricket_scorer_storage_v2',
    },
  ),
)
